import { intersection, orderBy, spread } from "lodash";
import { createSelector } from "reselect";

import { SaleSearchFields } from "constants/globalSearch";

import { getSaleStatus } from "lib/sale";

import { selectEffectiveSaleTitleBySaleIdLookup } from "../sales";
import { getActiveRole, getSales } from "../root";
import { getSaleSearchBySearchField } from "../settings";

const applyFilter = (sales, filterField, filterValues) => {
  const selectedSaleIds = Object.values(sales).map(
    sale => sale.livestocksale_id,
  );
  const filteredSaleIds = filterValues
    ? selectedSaleIds.filter(saleId =>
        filterValues.includes(sales[saleId][filterField]),
      )
    : selectedSaleIds;
  return filteredSaleIds;
};

const createFilterSelector = (filterField, searchField) =>
  createSelector(
    getSales,
    getSaleSearchBySearchField(searchField),
    (sales, filterValues) => applyFilter(sales, filterField, filterValues),
  );

export const selectSaleIdFilteredSaleIds = createFilterSelector(
  "livestocksale_id",
  SaleSearchFields.SaleId,
);

export const selectSpeciesFilteredSaleIds = createFilterSelector(
  "species_id",
  SaleSearchFields.Species,
);
export const selectSaleyardFilteredSaleIds = createFilterSelector(
  "saleyard_name",
  SaleSearchFields.Saleyard,
);
export const selectSaleTypeFilteredSaleIds = createFilterSelector(
  "sale_type",
  SaleSearchFields.SaleType,
);
export const selectSaleTitleFilteredSaleIds = createSelector(
  [
    getSales,
    selectEffectiveSaleTitleBySaleIdLookup,
    getSaleSearchBySearchField(SaleSearchFields.Title),
  ],
  (sales, effectiveTitleBySaleId, filterValues) => {
    const selectedSaleIds = Object.values(sales).map(
      sale => sale.livestocksale_id,
    );
    if (!filterValues || filterValues.length === 0) {
      return selectedSaleIds;
    }
    return selectedSaleIds.filter(saleId =>
      filterValues.includes(effectiveTitleBySaleId[saleId]),
    );
  },
);
export const selectSaleDateFilteredSaleIds = createFilterSelector(
  "date",
  SaleSearchFields.Date,
);

export const selectSaleStatusFilteredSaleIds = createSelector(
  getSales,
  getSaleSearchBySearchField(SaleSearchFields.Status),
  getActiveRole,
  (sales, filterValues, role) => {
    const selectedSaleIds = filterValues
      ? Object.values(sales).reduce((result, sale) => {
          const deploymentStatusId =
            sale.deployment_sales[0]?.deploymentStatusId;
          const baseSaleStatus = getSaleStatus(sale, role);
          if (
            filterValues.includes(deploymentStatusId) ||
            filterValues.includes(baseSaleStatus)
          ) {
            result.push(sale.livestocksale_id);
          }
          return result;
        }, [])
      : Object.values(sales).map(sale => sale.livestocksale_id);

    return selectedSaleIds;
  },
);

export const selectFilteredSaleIds = createSelector(
  [
    selectSaleIdFilteredSaleIds,
    selectSaleTitleFilteredSaleIds,
    selectSpeciesFilteredSaleIds,
    selectSaleyardFilteredSaleIds,
    selectSaleTypeFilteredSaleIds,
    selectSaleDateFilteredSaleIds,
    selectSaleStatusFilteredSaleIds,
  ],
  (...filterResults) => {
    const combinedSaleIds = spread(intersection)(filterResults);
    return combinedSaleIds;
  },
);

export const selectSaleIdsGroupedByPastAndFuture = createSelector(
  [getSales, selectFilteredSaleIds],
  (sales, filteredSaleIds) => {
    const pastSaleIds = [];
    const futureSaleIds = [];

    const filteredSales =
      filteredSaleIds && filteredSaleIds.length > 0
        ? filteredSaleIds.map(saleId => sales[saleId]).filter(Boolean)
        : [];

    const orderedSales = orderBy(filteredSales, "date", "desc");

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    Object.values(orderedSales).forEach(sale => {
      const saleDate = new Date(sale.date);
      if (saleDate < yesterday) {
        pastSaleIds.push(sale.livestocksale_id);
      } else {
        futureSaleIds.push(sale.livestocksale_id);
      }
    });

    return [pastSaleIds, futureSaleIds];
  },
);
